import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ONBOARDING_CARD_DISMISSED_EVENT,
  ONBOARDING_CARD_IDS,
  type OnboardingCardId,
  dismissOnboardingCard,
  remainingOnboardingCards,
} from '../../lib/onboardingCardDismissal';
import { cn } from '../../lib/utils';

import AddWidgetCard from './AddWidgetCard';
import InvitePeopleCard from './InvitePeopleCard';
import WorkspaceBackgroundPicker from './WorkspaceBackgroundPicker';

// Deck geometry, straight off the Figma spec: the front card is 472 wide, the one behind
// it 448, the one behind that 424 — 24px narrower each step, so a 12px gutter per side —
// and each sits 16px higher than the card in front of it.
//
// Those are uniform scales of the front card (448/472, 424/472), so a transform from the
// top centre reproduces the whole step at once: the spec's 12 → 11.39 → 10.78 radii and
// 0.5 → 0.475 → 0.449 borders fall out of the scale rather than needing their own values.
//
// Index 0 is the front slot. Every card in the deck is a real card parked at one of these
// slots; advancing just changes which slot each one is assigned, and the shared transition
// carries them between slots — that's what makes the card behind zoom forward.
const SLOT_SCALES = [1, 448 / 472, 424 / 472];
const SLOT_OFFSET_PX = 16;
const VISIBLE_SLOTS = SLOT_SCALES.length;

// How long a card takes to move between slots, and to slide out when retired. The removal
// timer matches so a dismissed card isn't unmounted mid-slide.
const CARD_TRANSITION_MS = 500;

// The slow path: a deliberate drag has to travel this far to count. Flicks don't need it —
// they're caught by velocity below — so this can be a committed distance rather than a
// hair trigger compensating for throws that distance alone would miss.
const SWIPE_THRESHOLD_PX = 64;
// Trackpad horizontal scroll arrives as wheel deltas, not pointer moves, so it needs its
// own accumulator: enough travel to be deliberate, then a cooldown so one long two-finger
// push advances a single card instead of flying through the whole deck.
const WHEEL_THRESHOLD_PX = 180;
const WHEEL_COOLDOWN_MS = 600;
const WHEEL_IDLE_RESET_MS = 150;
// Past this much the pointer is dragging, not clicking — used to swallow the click that
// would otherwise fire on whichever tile the finger happened to land on.
const DRAG_SLOP_PX = 5;
// The card trails the finger at this fraction past the slop, so a stray nudge barely
// registers and reaching the threshold feels like pulling against something.
const DRAG_RESISTANCE = 0.6;
// Hard ceiling on how far the card follows. Without it a long drag walks the card clean off
// the screen; past this the pull just stops giving, the way a rubber band runs out.
const DRAG_MAX_PX = 48;
// A flick this fast advances the deck no matter how short it was. Distance alone can't tell
// a deliberate throw from a slow nudge of the same length, which is what made quick swipes
// feel like they were being ignored. Roughly a third of a screen-width per second.
const FLICK_VELOCITY_PX_PER_MS = 0.35;
// A card being retired zooms out past the rear slot as it goes, rather than sliding off to
// one side — same axis as the cycle, so dismissing reads as the deck's own motion.
const RETIRE_SCALE = 0.86;
// Every card is pinned to the spec's 160px so swapping the front card can't resize the
// deck — the slots measure against this box, and the feed below it holds still.
const CARD_HEIGHT_CLASS = 'h-40';

// The background card is the strictest consumer of the workspace shape, so borrow its type
// rather than declaring a looser one the picker would then reject.
type BackgroundPickerProps = React.ComponentProps<typeof WorkspaceBackgroundPicker>;
type StackWorkspace = BackgroundPickerProps['workspace'];

interface OnboardingCardStackProps {
  /** Save-path overrides for the background card — DMs write through their own endpoint. */
  backgroundSaveAttachment?: BackgroundPickerProps['saveAttachment'];
  backgroundSavePreset?: BackgroundPickerProps['savePreset'];
  /** Which cards this deck deals, in order — DMs pass a deck without the invite card. */
  cardIds?: readonly OnboardingCardId[];
  className?: string;
  onEmpty?: () => void;
  profileId?: string | null;
  workspace: StackWorkspace;
}

function OnboardingCardStack({
  backgroundSaveAttachment,
  backgroundSavePreset,
  cardIds = ONBOARDING_CARD_IDS,
  className,
  onEmpty,
  profileId,
  workspace,
}: OnboardingCardStackProps) {
  const workspaceId = workspace?.id ? String(workspace.id) : null;
  const [remaining, setRemaining] = useState<OnboardingCardId[]>(() => remainingOnboardingCards(workspaceId, profileId, cardIds));
  const [activeIndex, setActiveIndex] = useState(0);
  const [exitingCardId, setExitingCardId] = useState<OnboardingCardId | null>(null);
  // Read by the dismissal listener, which would otherwise yank the card mid-animation.
  const exitingCardIdRef = useRef<OnboardingCardId | null>(null);
  exitingCardIdRef.current = exitingCardId;
  const exitTimerRef = useRef<number | null>(null);
  const dragStartXRef = useRef<number | null>(null);
  const didDragRef = useRef(false);
  // Last sampled position and the smoothed horizontal speed at that moment.
  const lastPointRef = useRef<{ t: number; x: number } | null>(null);
  const velocityRef = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const swipeRef = useRef<HTMLDivElement | null>(null);
  const suppressedSelectionRef = useRef(false);

  // A horizontal drag across text reads to the browser as "select this", which both paints
  // the page in highlight and steals the rest of the gesture. Kill selection for as long as
  // the drag lasts, then hand it straight back.
  const suppressSelection = useCallback(function suppressSelection() {
    if (suppressedSelectionRef.current) return;
    suppressedSelectionRef.current = true;
    document.body.style.userSelect = 'none';
    window.getSelection()?.removeAllRanges();
  }, []);

  const restoreSelection = useCallback(function restoreSelection() {
    if (!suppressedSelectionRef.current) return;
    suppressedSelectionRef.current = false;
    document.body.style.userSelect = '';
  }, []);

  useEffect(
    function restoreSelectionOnUnmount() {
      return () => restoreSelection();
    },
    [restoreSelection],
  );

  useEffect(function clearExitTimerOnUnmount() {
    return () => {
      if (exitTimerRef.current !== null) window.clearTimeout(exitTimerRef.current);
    };
  }, []);

  // Re-read on scope change so switching workspaces doesn't carry the previous deck over.
  useEffect(
    function resetForScope() {
      setRemaining(remainingOnboardingCards(workspaceId, profileId, cardIds));
      setActiveIndex(0);
    },
    [cardIds, profileId, workspaceId],
  );

  // The background card can also be dismissed from the settings panel and DM preferences,
  // which write the same key without going through this component.
  useEffect(
    function syncExternalDismissals() {
      function handleDismissed() {
        // Our own dismissals fire this too; let the exit animation finish and drop it there.
        if (exitingCardIdRef.current) return;
        setRemaining(remainingOnboardingCards(workspaceId, profileId, cardIds));
      }

      window.addEventListener(ONBOARDING_CARD_DISMISSED_EVENT, handleDismissed);
      return () => window.removeEventListener(ONBOARDING_CARD_DISMISSED_EVENT, handleDismissed);
    },
    [cardIds, profileId, workspaceId],
  );

  useEffect(
    function reportEmptied() {
      if (remaining.length === 0) onEmpty?.();
    },
    [onEmpty, remaining.length],
  );

  // Deck order as seen from the front, with the retiring card lifted out. Dropping it here
  // rather than waiting for the removal timer is what lets the card behind start moving
  // forward at the same moment the retiring one starts sliding away.
  const stackOrder = useMemo(() => {
    if (remaining.length === 0) return [];

    const start = activeIndex % remaining.length;
    const rotated = [...remaining.slice(start), ...remaining.slice(0, start)];
    return rotated.filter((cardId) => cardId !== exitingCardId);
  }, [activeIndex, exitingCardId, remaining]);

  const handleDismiss = useCallback(
    function handleDismiss(cardId: OnboardingCardId) {
      if (exitingCardIdRef.current) return;

      // Persist straight away so a reload mid-animation still counts it as dismissed.
      dismissOnboardingCard(cardId, workspaceId, profileId);
      setExitingCardId(cardId);

      exitTimerRef.current = window.setTimeout(() => {
        setRemaining((current) => current.filter((id) => id !== cardId));
        setExitingCardId(null);
        // The deck already rotated visually; reset so slot 0 is the list head again.
        setActiveIndex(0);
        exitTimerRef.current = null;
      }, CARD_TRANSITION_MS);
    },
    [profileId, workspaceId],
  );

  const handleDismissBackground = useCallback(() => handleDismiss('background-image'), [handleDismiss]);
  const handleDismissInvite = useCallback(() => handleDismiss('invite-people'), [handleDismiss]);
  const handleDismissWidget = useCallback(() => handleDismiss('add-widget'), [handleDismiss]);

  // Swiping only browses the deck — a card is retired by its X or by acting on it, never
  // by being flicked past, so an idle swipe can't silently burn one.
  // Every input lands here — drag, trackpad, click — so all three have the identical effect:
  // the front card zooms out into the rear slot while the cards behind zoom forward, one
  // shared transition carrying all of them.
  //
  // Direction deliberately does not matter. Cycling the other way only moves the front card
  // back one slot rather than to the rear, which reads as a different (and broken) animation
  // depending on which way you happened to swipe. A gesture either advances the deck or it
  // doesn't; with three cards you reach any of them either way.
  const advanceDeck = useCallback(
    function advanceDeck() {
      const count = remaining.length;
      if (count < 2) return;

      setActiveIndex((current) => (current + 1) % count);
    },
    [remaining.length],
  );

  // The gesture runs on window listeners for its whole life rather than on the card's own
  // pointer events. Element-bound handling needs setPointerCapture to survive the cursor
  // leaving the card, and that capture kept swallowing the end of the gesture — releasing it
  // fires lostpointercapture synchronously, so the release path raced its own cleanup and
  // the drag would finish without ever advancing. Window listeners can't miss the release.
  const handlePointerDown = useCallback(
    function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (remaining.length < 2) return;

      dragStartXRef.current = event.clientX;
      didDragRef.current = false;
      lastPointRef.current = { t: event.timeStamp, x: event.clientX };
      velocityRef.current = 0;
      setIsDragging(true);
    },
    [remaining.length],
  );

  useEffect(
    function trackDragOnWindow() {
      if (!isDragging) return undefined;

      function handleMove(event: PointerEvent) {
        const startX = dragStartXRef.current;
        if (startX === null) return;

        const last = lastPointRef.current;
        if (last) {
          const elapsed = event.timeStamp - last.t;
          // Smoothed rather than instantaneous: single pointer samples are jumpy enough that
          // one stray frame would otherwise read as a flick.
          if (elapsed > 0) velocityRef.current = velocityRef.current * 0.6 + ((event.clientX - last.x) / elapsed) * 0.4;
        }
        lastPointRef.current = { t: event.timeStamp, x: event.clientX };

        const deltaX = event.clientX - startX;
        if (Math.abs(deltaX) <= DRAG_SLOP_PX) {
          setDragOffset(0);
          return;
        }

        didDragRef.current = true;
        suppressSelection();
        // The browser may already have started a selection before we crossed the slop.
        window.getSelection()?.removeAllRanges();
        const past = deltaX - Math.sign(deltaX) * DRAG_SLOP_PX;
        const followed = past * DRAG_RESISTANCE;
        setDragOffset(Math.sign(followed) * Math.min(Math.abs(followed), DRAG_MAX_PX));
      }

      function handleEnd(event: PointerEvent) {
        const startX = dragStartXRef.current;
        const velocity = velocityRef.current;

        dragStartXRef.current = null;
        lastPointRef.current = null;
        velocityRef.current = 0;
        setIsDragging(false);
        restoreSelection();
        setDragOffset(0);

        if (startX === null || event.type === 'pointercancel') return;

        const deltaX = event.clientX - startX;
        // Fast throw advances on its own; a slow drag has to cover the distance.
        const flicked = Math.abs(velocity) >= FLICK_VELOCITY_PX_PER_MS && Math.abs(deltaX) > DRAG_SLOP_PX;
        if (!flicked && Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;

        advanceDeck();
      }

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleEnd);
      window.addEventListener('pointercancel', handleEnd);
      return () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleEnd);
        window.removeEventListener('pointercancel', handleEnd);
      };
    },
    [advanceDeck, isDragging, restoreSelection, suppressSelection],
  );

  // Capture phase, and bound to every card rather than just the front one: by the time the
  // trailing click arrives the deck has already advanced, so the card you dragged is now a
  // back card whose own handler would advance it a second time.
  const handleClickCapture = useCallback(function handleClickCapture(event: React.MouseEvent<HTMLDivElement>) {
    if (!didDragRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    didDragRef.current = false;
  }, []);

  // Trackpad horizontal scroll. Bound natively rather than via onWheel because React's
  // wheel listener is passive — preventDefault there is ignored, and without it the
  // browser hands the gesture to the page and swipes back through history instead.
  useEffect(
    function listenForHorizontalScroll() {
      const node = swipeRef.current;
      if (!node || remaining.length < 2) return undefined;

      let travel = 0;
      let idleTimer: number | null = null;
      let cooling = false;

      function handleWheel(event: WheelEvent) {
        // Vertical intent belongs to the feed; only claim clearly horizontal gestures.
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();
        if (cooling) return;

        travel += event.deltaX;
        if (idleTimer !== null) window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(() => {
          travel = 0;
          setDragOffset(0);
        }, WHEEL_IDLE_RESET_MS);

        if (Math.abs(travel) < WHEEL_THRESHOLD_PX) {
          // Follow the scroll the same way the card follows a finger: scrolling right pushes
          // the card left, and it springs back if you stop short of the threshold.
          const followed = -travel * DRAG_RESISTANCE;
          setDragOffset(Math.sign(followed) * Math.min(Math.abs(followed), DRAG_MAX_PX));
          return;
        }

        advanceDeck();
        travel = 0;
        setDragOffset(0);
        cooling = true;
        window.setTimeout(() => {
          cooling = false;
        }, WHEEL_COOLDOWN_MS);
      }

      node.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        if (idleTimer !== null) window.clearTimeout(idleTimer);
        node.removeEventListener('wheel', handleWheel);
        setDragOffset(0);
      };
    },
    [advanceDeck, remaining.length],
  );

  function renderCard(cardId: OnboardingCardId) {
    if (cardId === 'background-image') {
      return (
        <WorkspaceBackgroundPicker
          workspace={workspace}
          profileId={profileId}
          hideOnDismiss={false}
          onChoiceApplied={handleDismissBackground}
          onDismiss={handleDismissBackground}
          saveAttachment={backgroundSaveAttachment}
          savePreset={backgroundSavePreset}
        />
      );
    }
    if (cardId === 'invite-people') return <InvitePeopleCard workspaceId={workspaceId} onDismiss={handleDismissInvite} />;
    return <AddWidgetCard workspaceId={workspaceId} onDismiss={handleDismissWidget} />;
  }

  if (remaining.length === 0) return null;

  return (
    <div className={cn('w-full max-w-118 shrink-0', className)} ref={swipeRef}>
      {/* Fixed height, not the front card's: the three cards aren't the same height, so
          measuring off whichever is in front made the whole deck resize on every swap. */}
      <div className={cn('relative', CARD_HEIGHT_CLASS)}>
        {remaining.map((cardId) => {
          const isExiting = cardId === exitingCardId;
          const slot = stackOrder.indexOf(cardId);
          const isFront = !isExiting && slot === 0;
          // Anything past the last slot parks behind the deepest card and fades out, so a
          // fourth card would emerge from under the stack rather than pop into existence.
          const clampedSlot = slot < 0 ? VISIBLE_SLOTS - 1 : Math.min(slot, VISIBLE_SLOTS - 1);
          const isHidden = !isExiting && slot >= VISIBLE_SLOTS;
          const transform = isExiting
            ? // Retiring continues the cycle's motion past the rear slot and fades, so it
              // reads as the card zooming away rather than being cut out of the deck.
              `translateY(-${VISIBLE_SLOTS * SLOT_OFFSET_PX}px) scale(${RETIRE_SCALE})`
            : `translateY(-${clampedSlot * SLOT_OFFSET_PX}px) scale(${SLOT_SCALES[clampedSlot]})` +
              (isFront && dragOffset ? ` translateX(${dragOffset}px)` : '');

          return (
            <div
              key={cardId}
              role={isFront || isExiting || isHidden ? undefined : 'button'}
              tabIndex={isFront || isExiting || isHidden ? undefined : 0}
              aria-label={isFront || isExiting || isHidden ? undefined : `Show next card`}
              aria-hidden={isHidden || undefined}
              onClick={isFront || isExiting || isHidden ? undefined : advanceDeck}
              onPointerDown={isFront ? handlePointerDown : undefined}
              onClickCapture={handleClickCapture}
              style={{
                transform,
                transformOrigin: 'top center',
                opacity: isExiting || isHidden ? 0 : 1,
                // Front card highest; each card further back sits under the one ahead of it.
                zIndex: VISIBLE_SLOTS - clampedSlot,
                // pan-y keeps the feed scrollable vertically while we claim the horizontal axis.
                touchAction: 'pan-y',
              }}
              className={cn(
                'absolute inset-x-0 top-0 select-none',
                // Suppressed mid-drag so the front card tracks the finger 1:1 rather than easing behind it.
                isFront && dragOffset ? null : 'transition-[transform,opacity] duration-500 ease-fluid motion-reduce:transition-none',
                // Only the retiring and off-deck cards are fully inert. A back card keeps
                // its own pointer events so its exposed top edge stays clickable — the
                // front card covers the rest and takes those clicks itself.
                isExiting || isHidden ? 'pointer-events-none' : null,
                !isFront && !isExiting && !isHidden && 'cursor-pointer',
              )}
            >
              {/* Back cards are inert scenery until promoted — their tiles must not be
                  clickable through the front card, and only their top edge is visible. */}
              <div className={isFront ? undefined : 'pointer-events-none'}>{renderCard(cardId)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default OnboardingCardStack;
